import subscriptions from "@services/subscriptions/instance";
import { isDev } from "@utils/general";
import React from "react";
// import type { Five9StateData } from "../types"; // unused import
import type { CallIdStateData } from "../types/callIdTypes";

const subscribeToCallFrom = (setCallFrom: (data: string | null) => void) => {
	subscriptions.subscribe("CallFrom", "Five9State", (data: string | null) => {
		setCallFrom(data);
	});
};

const subscribeToCallTo = (setCallTo: (data: string | null) => void) => {
	subscriptions.subscribe("CallTo", "Five9State", (data: string | null) => {
		setCallTo(data);
	});
};

const subscribeToAgentState = (
	setAgentState: (data: string | null) => void,
) => {
	subscriptions.subscribe("AgentState", "Five9State", (data: string | null) => {
		setAgentState(data);
	});
};

const subscribeToIsOutgoingCall = (
	setIsOutgoingCall: (data: boolean) => void,
) => {
	subscriptions.subscribe("IsOutgoingCall", "Five9State", (data: boolean) => {
		setIsOutgoingCall(data);
	});
};

const subscribeToAccountCode = (
	setAccountCode: (data: number | null) => void,
) => {
	subscriptions.subscribe(
		"AccountCode",
		"Five9State",
		(data: number | null) => {
			setAccountCode(data);
		},
	);
};

const subscribeToSessionId = (setSessionId: (data: string | null) => void) => {
	subscriptions.subscribe("SessionId", "Five9State", (data: string | null) => {
		setSessionId(data);
	});
};

const subscribeToEventId = (setEventId: (data: number | null) => void) => {
	subscriptions.subscribe("EventId", "Five9State", (data: number | null) => {
		setEventId(data);
	});
};

const subscribeToCallId = (setCallId: (data: string | null) => void) => {
	subscriptions.subscribe("CallId", "Five9State", (data: string | null) => {
		setCallId(data);
	});
};

const unsubscribeAll = () => {
	subscriptions.unsubscribe("CallFrom", "Five9State");
	subscriptions.unsubscribe("CallTo", "Five9State");
	subscriptions.unsubscribe("AgentState", "Five9State");
	subscriptions.unsubscribe("IsOutgoingCall", "Five9State");
	subscriptions.unsubscribe("AccountCode", "Five9State");
	subscriptions.unsubscribe("SessionId", "Five9State");
	subscriptions.unsubscribe("EventId", "Five9State");
	subscriptions.unsubscribe("CallId", "Five9State");
	isDev() && subscriptions.unsubscribe("Five9DevTest", "Five9State");
};

/**
 * Custom hook to manage Five9 subscription state with call ID support
 */
export const useFive9Subscriptions = (): CallIdStateData => {
	const [callFrom, setCallFrom] = React.useState<string | null>(null);
	const [callTo, setCallTo] = React.useState<string | null>(null);
	const [agentState, setAgentState] = React.useState<string | null>(null);
	const [accountCode, setAccountCode] = React.useState<number | null>(null);
	const [isOutgoingCall, setIsOutgoingCall] = React.useState<boolean>(false);
	const [sessionId, setSessionId] = React.useState<string | null>(null);
	const [eventId, setEventId] = React.useState<number | null>(null);

	// NEW: Call ID state management
	const [callId, setCallId] = React.useState<string | null>(null);
	const [callIdMappedSessionId, setCallIdMappedSessionId] = React.useState<
		string | null
	>(null);
	const [callIdResolutionStatus, setCallIdResolutionStatus] = React.useState<
		"pending" | "resolved" | "failed" | "fallback"
	>("pending");

	// Memoize subscription callbacks to prevent unnecessary re-subscriptions
	const handleCallFromChange = React.useCallback((data: string | null) => {
		setCallFrom(data);
	}, []);

	const handleCallToChange = React.useCallback((data: string | null) => {
		setCallTo(data);
	}, []);

	const handleAgentStateChange = React.useCallback((data: string | null) => {
		setAgentState(data);
	}, []);

	const handleAccountCodeChange = React.useCallback((data: number | null) => {
		setAccountCode(data);
	}, []);

	const handleIsOutgoingCallChange = React.useCallback((data: boolean) => {
		setIsOutgoingCall(data);
	}, []);

	const handleSessionIdChange = React.useCallback((data: string | null) => {
		setSessionId(data);
	}, []);

	const handleEventIdChange = React.useCallback((data: number | null) => {
		setEventId(data);
	}, []);

	const handleCallIdChange = React.useCallback((data: string | null) => {
		console.log(`🔍 [FIVE9_STATE] Call ID received: ${data}`);
		setCallId(data);

		// Reset resolution status when new call ID is received
		if (data) {
			setCallIdResolutionStatus("pending");
			setCallIdMappedSessionId(null);
		} else {
			setCallIdResolutionStatus("pending");
			setCallIdMappedSessionId(null);
		}
	}, []);

	React.useEffect(() => {
		console.log("🔄 [FIVE9_STATE] Setting up subscriptions...");

		// Subscribe to all telephony events with memoized callbacks
		subscribeToCallFrom(handleCallFromChange);
		subscribeToCallTo(handleCallToChange);
		subscribeToAgentState(handleAgentStateChange);
		subscribeToIsOutgoingCall(handleIsOutgoingCallChange);
		subscribeToAccountCode(handleAccountCodeChange);
		subscribeToSessionId(handleSessionIdChange);
		subscribeToEventId(handleEventIdChange);
		subscribeToCallId(handleCallIdChange); // NEW: Subscribe to call ID updates

		// Cleanup subscriptions on unmount
		return () => {
			console.log("🔄 [FIVE9_STATE] Cleaning up subscriptions...");
			unsubscribeAll();
		};
	}, [
		handleAccountCodeChange,
		handleAgentStateChange,
		handleCallFromChange,
		handleCallToChange,
		handleEventIdChange,
		handleIsOutgoingCallChange,
		handleSessionIdChange,
		handleCallIdChange, // NEW: Add call ID callback to dependencies
	]);

	return {
		callFrom,
		callTo,
		agentState,
		accountCode,
		isOutgoingCall,
		sessionId,
		eventId,
		// NEW: Call ID state properties
		callId,
		callIdMappedSessionId,
		callIdResolutionStatus,
	};
};
